import React, { useEffect, useState } from "react";

interface ObjectField {
  key: string;
  label: string;
  type: "text" | "number" | "select";
  options?: string[];
}

interface ObjectListEditorProps {
  label: string;
  items: any[];
  onChange: (updatedList: any[]) => void;
  disabled?: boolean;
  objectFields: ObjectField[];
  widthClass?: string;
}

const ObjectListEditor: React.FC<ObjectListEditorProps> = ({
  label,
  items,
  onChange,
  disabled,
  objectFields,
  widthClass = "w-60",
}) => {
  const [newItem, setNewItem] = useState<any>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editItem, setEditItem] = useState<any>({});

  const resetNewItem = () => {
    const empty = objectFields.reduce(
      (acc, f) => ({ ...acc, [f.key]: "" }),
      {}
    );
    setNewItem(empty);
  };

  useEffect(() => {
    resetNewItem();
  }, [objectFields]);

  const handleAdd = () => {
    // Allow adding as long as at least one field is filled
    const hasData = objectFields.some(
      (f) => newItem[f.key] && newItem[f.key] !== ""
    );
    if (!hasData) return;

    onChange([...items, { ...newItem }]);
    resetNewItem();
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...items];
    updated[editingIndex] = editItem;
    onChange(updated);
    setEditingIndex(null);
  };

  // Helper to determine if field should render as select
  const isSelectField = (f: ObjectField) =>
    String(f.type).toLowerCase() === "select" ||
    (f.options && f.options.length > 0);

  return (
    <div className="flex flex-col gap-2">
      {/* Add new item */}
      <div className="flex gap-2 items-center">
        {objectFields.map((f) =>
          isSelectField(f) ? (
            <select
              key={f.key}
              value={newItem[f.key] || ""}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  [f.key]: e.target.value,
                })
              }
              className={`${widthClass} p-1 h-8 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
              disabled={disabled}
            >
              <option value="">Select {f.label}</option>
              {(f.options || []).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              key={f.key}
              type={f.type}
              value={newItem[f.key] || ""}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  [f.key]:
                    f.type === "number"
                      ? Number(e.target.value)
                      : e.target.value,
                })
              }
              placeholder={f.label}
              className={`${widthClass} p-1 h-8 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
              disabled={disabled}
            />
          )
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={
            disabled ||
            !objectFields.some((f) => newItem[f.key] && newItem[f.key] !== "")
          }
        >
          Add
        </button>
      </div>

      {/* Existing items list */}
      {items.length > 0 && (
        <div className="flex flex-col gap-1 mt-2">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="flex gap-2 items-center bg-blue-100 text-blue-800 p-2 rounded"
            >
              {editingIndex === idx ? (
                <>
                  {objectFields.map((f) =>
                    isSelectField(f) ? (
                      <select
                        key={f.key}
                        value={editItem[f.key] || ""}
                        onChange={(e) =>
                          setEditItem({
                            ...editItem,
                            [f.key]: e.target.value,
                          })
                        }
                        className={`${widthClass} p-1 h-8 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                        disabled={disabled}
                      >
                        <option value="">Select {f.label}</option>
                        {(f.options || []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        key={f.key}
                        type={f.type}
                        value={editItem[f.key] || ""}
                        onChange={(e) =>
                          setEditItem({
                            ...editItem,
                            [f.key]:
                              f.type === "number"
                                ? Number(e.target.value)
                                : e.target.value,
                          })
                        }
                        className={`${widthClass} p-1 h-8 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                        disabled={disabled}
                      />
                    )
                  )}
                  <div className="flex flex-col gap-1 text-sm">
                    <button
                      type="button"
                      className="ml-1 text-green-600 hover:text-green-800"
                      onClick={handleSaveEdit}
                      disabled={disabled}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="ml-1 text-gray-600 hover:text-gray-800"
                      onClick={() => setEditingIndex(null)}
                      disabled={disabled}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {objectFields.map((f) => (
                    <span key={f.key} className={widthClass}>
                      {f.type === "text" && String(item[f.key]).length > 10
                        ? String(item[f.key]).substring(0, 10) + "..."
                        : item[f.key]}
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingIndex(idx);
                      setEditItem(item);
                    }}
                    className="ml-2 text-yellow-500 hover:text-yellow-700"
                    disabled={disabled}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange(items.filter((_, i) => i !== idx))}
                    className="ml-2 text-red-500 hover:text-red-700"
                    disabled={disabled}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ObjectListEditor;
